import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { api, type User } from '@wellesley/openapi'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import { getStore } from '@wellesley/store'

const membersApi = api.enhanceEndpoints({
  endpoints: {
    getApiV1GroupsByGroupIdMembership: {
      providesTags: (_result, _error, { groupId }) => [
        { type: 'GroupMembership', id: groupId },
        'USER_CHANGED',
      ],
    },
    deleteApiV1GroupsByGroupIdMembership: {
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'GroupMembership', id: groupId },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    getApiV1GroupsByGroupIdMembers: {
      providesTags: ['GroupMembers', 'USER_CHANGED'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1GroupsByGroupIdMembers: {
      async onQueryStarted(args, { queryFulfilled }) {
        await handleBasicResponse(
          queryFulfilled,
          `User ${args.groupMemberCreate.memberId} has been added to group`,
        )
      },
    },
    deleteApiV1GroupsByGroupIdMembersAndMemberId: {
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'GroupMembers', id: groupId },
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled, 'You have left the group')
      },
    },
  },
})

export const {
  useGetApiV1GroupsByGroupIdMembershipQuery,
  useDeleteApiV1GroupsByGroupIdMembershipMutation,
  useGetApiV1GroupsByGroupIdMembersQuery,
  usePostApiV1GroupsByGroupIdMembersMutation,
  useDeleteApiV1GroupsByGroupIdMembersAndMemberIdMutation,
} = membersApi

// TODO: remove this once we have a proper users API which will return full user info
const membersApiExtras = api.injectEndpoints({
  endpoints: (build) => ({
    getGroupFullMembers: build.query<User[], { groupId: string }>({
      async queryFn({ groupId }) {
        if (!groupId) {
          return { data: [] }
        }

        try {
          const store = getStore()

          const members = await store
            .dispatch(
              api.endpoints.getApiV1GroupsByGroupIdMembers.initiate({
                groupId,
              }),
            )
            .unwrap()

          if (!Array.isArray(members) || members.length === 0) {
            return { data: [] }
          }

          const usersResult = await Promise.allSettled(
            members.map((member) =>
              store
                .dispatch(
                  api.endpoints.getApiV1UsersByUserId.initiate({
                    userId: member.memberId,
                    full: true,
                    htmlContent: true,
                  }),
                )
                .unwrap(),
            ),
          )

          const users = usersResult.flatMap((result) =>
            result.status === 'fulfilled' && result.value
              ? [result.value as User]
              : [],
          )

          return { data: users }
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)

          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `Failed to fetch group member users: ${message}`,
            } as FetchBaseQueryError,
          }
        }
      },
      providesTags: (_result, _error, { groupId }) => [
        { type: 'GroupMembers', id: groupId },
      ],
    }),
  }),
  overrideExisting: false,
})

export const { useGetGroupFullMembersQuery } = membersApiExtras
